import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Divider, MenuProps, Input, message } from "antd";
import TextArea from "antd/es/input/TextArea";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { setGenerateSectionWithAI, selectGenerateSectionWithAI, setSelectedKitTemplateData, selectSelectedKitTemplateData, setIsEditingKit, setSelectedTemplate, selectSelectedKit, selectSelectedTemplate, selectIsEvaluationQuestionsModal, setIsEvaluationQuestionsModal, setInterviewKitNameModalOpen, selectIsEditingKit, setIsSuccessSavingEvaluationKitTosStage, selectEvaluationActionType, selectIsStartingWithEvaluationKit } from "../../../../../interview/slice/interviewSlice";
import { useLazyGetKitTemplateByIdQuery, useUpdateKitMutation } from "../../../../../interview/slice/interviewApiSlice";
import { GenerateNewSection } from "../../../../../interview/components/kit/GenerateNewSection";
import LazyLoader from "../../../../../../shared/components/LazyLoader/load";
import ModalComponent from "../../../../../../shared/components/Modals/ModalComponent";
import ModalBody from "../../../../../../shared/components/Modals/ModalBody";
import AddSectionDropdown from "../../../../../../shared/components/Dropdown/AddSectionDropdown";
import BulletSkeleton from "../../../../../../shared/components/EditMode/BulletSkeleton";
import { DraggableBullet } from "../../../../../../shared/components/EditMode/DraggableBullet";
import { apiStatuses, errorMessage, newSectionItems } from "../../../../../../shared/utils/constantData";
import { IKit, IKitQuestion, IKitSection, IMetrics } from "../../../../../../shared/utils/interfaces/interviewKit";
import { Warning } from "../../../../../../shared/components/Warning/Warning";
import { getImageUrl } from "../../../../../../shared/utils/functions/images"; 
import { ReactComponent as Exclamation } from '../../../../../../shared/assets/images/Exclamation.svg'
import EvaluationFooterActions from "./EvaluationFooterActions";
import { useParams } from "react-router-dom";
import { selectCandidateProfile, setCandidateProfile } from "../../../../slice/candidateSlice";
import { EvaluationActionType } from "../../../../../../shared/utils/interfaces/evaluation";

export const EvaluationQuestionsModal = () => {
    const dispatch = useDispatch()
    const { jobId } = useParams();
    const isGeneratingSection = useSelector(selectGenerateSectionWithAI);
    const selectedKit = useSelector(selectSelectedKit);
    const selectedTemplate = useSelector(selectSelectedTemplate);
    const kitTemplateData = useSelector(selectSelectedKitTemplateData)
    const isEvaluationQuestionsModal = useSelector(selectIsEvaluationQuestionsModal);
    const isEditingKit = useSelector(selectIsEditingKit);
    const candidateStageId = useSelector(selectCandidateProfile)?.pipelineHiringStageId
    const evaluationActionType = useSelector(selectEvaluationActionType)
    const candidate = useSelector(selectCandidateProfile)
    const isStartingWithEvaluationKit = useSelector(selectIsStartingWithEvaluationKit)
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor), useSensor(KeyboardSensor))
    const [dragContent, setDragContent] = useState<string | null>(null)
    const lastBulletRef = useRef<HTMLInputElement>(null);
    const [focusedBullet, setFocusedBullet] = useState<boolean>(false);
    const [getKitTemplateById, { isFetching: isFetchingTemplateData, isError: isErrorTemplateData, data: templateData, isSuccess: isSuccessTemplateData }] = useLazyGetKitTemplateByIdQuery();
    const [updateKit, { isLoading: isLoadingSaving, isError: isErrorSaving, isSuccess: isSuccessSaving, error: savingError }] = useUpdateKitMutation();

    useEffect(() => {
        const getData = async () => {
            if (selectedTemplate?.key) {
                await getKitTemplateById(selectedTemplate?.key)
            }
        }
        getData()
        // eslint-disable-next-line
    }, [selectedTemplate, dispatch]);

    useEffect(() => {
        if (selectedTemplate?.key && isSuccessTemplateData && isEvaluationQuestionsModal) {
            dispatch(setSelectedKitTemplateData((templateData?.data)))
            dispatch(setIsEditingKit((false)))
        }
        else if (selectedKit?.key && isEvaluationQuestionsModal) {
            dispatch(setIsEditingKit((false)))
        }
        //eslint-disable-next-line
    }, [isSuccessTemplateData, templateData, selectedKit, isEvaluationQuestionsModal, dispatch])

    useEffect(() => {
        if (isSuccessSaving) {
            dispatch(setCandidateProfile({ ...candidate, hasKit: true }))
            if (evaluationActionType !== EvaluationActionType.sendAI && !isStartingWithEvaluationKit) {
                message.success('Interview kit Updated Successfully', 2)
            closeModal()
                dispatch(setIsSuccessSavingEvaluationKitTosStage(true))
            }
            dispatch(setIsEditingKit((false)))
            setFocusedBullet(false);
        }
        //eslint-disable-next-line
    }, [isSuccessSaving, evaluationActionType])

    useEffect(() => {
        if (isErrorSaving) {
            dispatch(setIsEditingKit(false))
            message.error(savingError?.status === apiStatuses.badRequest ? errorMessage : savingError?.data?.message || errorMessage, 5)
        }
    }, [isErrorSaving, savingError, dispatch])

    useEffect(() => {
        if (!focusedBullet) {
            lastBulletRef.current?.focus();
            setFocusedBullet(true);
        }
    }, [lastBulletRef, focusedBullet]);

    const updatedKitTemplateDataWithQuestions = (sectionId: number, updatedQuestions: IKitQuestion[]) => {
        dispatch(setIsEditingKit((true)))
        const updatedKitTemplate = {
            ...kitTemplateData,
            data: {
                title: kitTemplateData?.data.title,
                sections: {
                    evaluationMetrics: kitTemplateData?.data?.sections?.evaluationMetrics.map((section) => sectionId === section?.id ? { ...section, metrics: updatedQuestions } : section)
                }
            }
        }
        dispatch(setSelectedKitTemplateData((updatedKitTemplate as IKit)))
    }

    const updatedKitTemplateDataWithSections = (updatedSections: IKitSection[]) => {
        dispatch(setIsEditingKit((true)))
        const updatedKitTemplate = {
            ...kitTemplateData,
            data: {
                ...kitTemplateData.data,
                sections: {
                    ...kitTemplateData?.data?.sections,
                    evaluationMetrics: updatedSections,
                },
            },
        }
        dispatch(setSelectedKitTemplateData((updatedKitTemplate as IKit)))
    }

    const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>, index: number, sectionId: number) => {
        const updatedQuestion = kitTemplateData?.data?.sections?.evaluationMetrics.find(({ id }) => id === sectionId)?.metrics?.map((metric, bulletIndex) => index === bulletIndex ? { ...metric, question: event?.target?.value } : metric)
        updatedKitTemplateDataWithQuestions(sectionId, updatedQuestion as IKitQuestion[])
    };

    const handleDeleteQuestion = (index: number, sectionId: number) => {
        const updatedQuestion = kitTemplateData?.data?.sections?.evaluationMetrics.find(({ id }) => id === sectionId)?.metrics?.filter((_, bulletIndex) => index !== bulletIndex)
        updatedKitTemplateDataWithQuestions(sectionId, updatedQuestion as IKitQuestion[])
    };

    const handleAddQuestion = (sectionId: number) => {
        setFocusedBullet(false);
        const questions = kitTemplateData?.data?.sections?.evaluationMetrics.find(({ id }) => id === sectionId)?.metrics
        const updatedQuestion = [...questions as IMetrics[], { metric: '', question: '', isNewQuestion: true }]
        updatedKitTemplateDataWithQuestions(sectionId, updatedQuestion as IKitQuestion[])
        setTimeout(() => lastBulletRef?.current?.focus(), 0);
    };

    const onDragStart = (event: any, sectionId: number) => {
        const questions = kitTemplateData?.data?.sections?.evaluationMetrics.find(({ id }) => id === sectionId)?.metrics
        if (questions?.length) setDragContent(questions[Number(event.active.id)].question);
    }

    const onDragEnd = (event: any, sectionId: number) => {
        setDragContent(null);
        const { active, over } = event;
        if (!over) return;
        if (active?.id !== over?.id) {
            const questions = kitTemplateData?.data?.sections?.evaluationMetrics.find(({ id }) => id === sectionId)?.metrics
            const oldIndex = questions?.findIndex((input, index) => index.toString() === active?.id) as number
            const newIndex = questions?.findIndex((input, index) => index.toString() === over?.id) as number
            const updatedQuestion = arrayMove(questions as IMetrics[], oldIndex, newIndex);
            updatedKitTemplateDataWithQuestions(sectionId, updatedQuestion as IKitQuestion[])
        };
    }

    const closeModal = () => {
        dispatch(setSelectedTemplate({}))
        dispatch(setGenerateSectionWithAI(false));
        dispatch(setIsEvaluationQuestionsModal(false))
    };

    const handleClearAll = (id: number) => {
        const updatedSections = kitTemplateData?.data?.sections?.evaluationMetrics.map((section) => section?.id === id ? { ...section, metrics: [] } : section)
        updatedKitTemplateDataWithSections(updatedSections)
    };

    const handleDelete = (id: number) => {
        const updatedSections = kitTemplateData?.data?.sections?.evaluationMetrics.filter((section) => section?.id !== id);
        updatedKitTemplateDataWithSections(updatedSections)
    };

    const addNewSection = () => {
        if (kitTemplateData?.data?.sections?.evaluationMetrics) {
            let AllSections = [...kitTemplateData?.data?.sections?.evaluationMetrics];
            const newSection = {
                id: Date.now(),
                title: "",
                metrics: [],
                isNew: true,
            }
            AllSections = [...AllSections, newSection]
            updatedKitTemplateDataWithSections(AllSections)
        }
    }

    const handleSelectNewSectionItem: MenuProps['onClick'] = (e) => {
        if (e?.key === "1") {
            dispatch(setGenerateSectionWithAI(true))
        }
        else if (e?.key === "2") {
            addNewSection()
        }
    }
    const handleNewSectionTitle = (value: string, id: number) => {
        const updatedSections = kitTemplateData?.data?.sections?.evaluationMetrics.map((section) =>
            section?.id === id
                ? { ...section, title: value, hasNoTitle: value.trim() === '' }
                : section
        );
        // const updatedSections = kitTemplateData?.data?.sections?.evaluationMetrics.map((section) => section?.id === id ? { ...section, title: value } : section)
        updatedKitTemplateDataWithSections(updatedSections)
    };

    const sectionsTitlesValidation = () => {
        const updatedSections = kitTemplateData?.data?.sections?.evaluationMetrics.map((section) => !section?.title ? { ...section, hasNoTitle: true } : { ...section, hasNoTitle: false })
        updatedKitTemplateDataWithSections(updatedSections)
        return kitTemplateData?.data?.sections?.evaluationMetrics.every(({ title }) => title)
    }

    const saveAsTemplate = () => {
        const isSectionsHavingTitle = sectionsTitlesValidation()
        if (isSectionsHavingTitle) {
            dispatch(setInterviewKitNameModalOpen(true))
            closeModal();
        }
    }

    const isSaveBtnDisabled = useMemo(() => {
        const isSectionHavingQuestions = kitTemplateData?.data?.sections?.evaluationMetrics.every(({ metrics }) => metrics?.length && metrics.every(({ question }) => question))
        return isFetchingTemplateData || isLoadingSaving || !isSectionHavingQuestions || !isEditingKit
    }, [isFetchingTemplateData, isLoadingSaving, kitTemplateData, isEditingKit])

    const handleSave = async () => {
        const { id, ...rest } = kitTemplateData
        const isSectionsHavingTitle = sectionsTitlesValidation()
        if (isSectionsHavingTitle) {
            await updateKit({ ...rest, jobId: Number(jobId), stageId: candidateStageId, kitId: selectedKit?.key ? selectedKit?.key : undefined })
        }
        if (!isSectionsHavingTitle) message.error('Missing section title')
    }

    return (
         <ModalComponent
            visibleModal={isEvaluationQuestionsModal}
            handleCloseModal={closeModal}
            width={1010}
            content={
                <ModalBody title={!isFetchingTemplateData &&
                    <div className="flex mb-6 text-lg font-semibold text-gray-600 capitalize md:flex-row">
                        {kitTemplateData?.data?.title}
                    </div>
                }>
                    <>
                        {(isFetchingTemplateData) ? 
                            <div className="w-full h-[34.5rem] flex flex-col justify-center items-center">
                                <LazyLoader />
                                <h3 className="mt-2 text-indigo-700">{selectedKit?.key ? 'Retrieving Kit..': 'Retrieving Template..' }</h3>
                            </div>
                            : kitTemplateData?.id && (!isFetchingTemplateData) ?
                                <div className="h-[34.5rem] overflow-y-scroll">
                                    {kitTemplateData?.data?.sections?.evaluationMetrics?.map(({ id, title, metrics, isNew, hasNoTitle }) => {
                                        const questions = metrics?.map(({ question, isNewQuestion }: IKitQuestion) => {
                                            return { question, isNewQuestion }
                                        })
                                        return <div key={id} className="px-5 mb-6">
                                            <div className="flex justify-between">
                                                {/* <p className="mt-2 text-xs font-semibold text-gray-900 uppercase">{title}</p> */}
                                                <div className="flex items-center w-full">
                                                    <Input
                                                        className={`mt-2 text-xs font-semibold text-gray-900 uppercase border-none transition-none ${hasNoTitle ? 'w-1/5' : 'w-full'}`}
                                                        defaultValue={title}
                                                        readOnly={!isNew}
                                                        placeholder="Write Section title"
                                                        onChange={(e) => handleNewSectionTitle(e?.target?.value, id)}
                                                    />
                                                    {hasNoTitle && (
                                                        <div className="flex items-center w-1/4 gap-1 mt-2">
                                                            <Exclamation />
                                                            <p className="text-sm font-semibold text-red-800">Missing section title</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="whitespace-nowrap">
                                                    <Button
                                                        htmlType="button"
                                                        type="link"
                                                        className="pr-0 text-sm font-medium text-gray-500"
                                                        // disabled={}
                                                        onClick={() => handleClearAll(id)}
                                                    >
                                                        Clear All
                                                    </Button>
                                                    <Divider type="vertical" className='h-5 ml-4 mr-2.5 border-gray-400' />
                                                    <img
                                                        src={getImageUrl('Trash')}
                                                        alt='Delete'
                                                        className='inline cursor-pointer'
                                                        onClick={() => handleDelete(id)}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <DndContext collisionDetection={closestCenter} autoScroll={false} onDragStart={(e) => onDragStart(e, id)} onDragEnd={(e) => onDragEnd(e, id)} sensors={sensors}>
                                                    <SortableContext items={questions.map(({ question }) => question)} strategy={verticalListSortingStrategy}>
                                                        {Array.isArray(questions) && questions?.map(({ question, isNewQuestion }, index) => (
                                                            <DraggableBullet
                                                                key={index}
                                                                index={index}
                                                                inputValue={question}
                                                                handleDeleteInput={() => handleDeleteQuestion(index, id)}
                                                                handleInputChange={(e) => handleQuestionChange(e, index, id)}
                                                                ref={index === metrics.length - 1 && isNewQuestion ? lastBulletRef : null}
                                                            />
                                                        ))}
                                                    </SortableContext>
                                                    <DragOverlay>
                                                        {dragContent ? (
                                                            <BulletSkeleton dragContent={dragContent} withActionPointsIcon={false} />
                                                        ) : null}
                                                    </DragOverlay>
                                                </DndContext>
                                                <div className='h-10 px-0 bg-white md:h-auto'>
                                                    <div className='flex border-y border-neutral-200'>
                                                        <img src={getImageUrl('grayPoint')} alt='point' className='ml-[0.4rem] mr-[0.38rem]' />
                                                        <TextArea autoSize readOnly bordered={false} onClick={() => handleAddQuestion(id)} className='px-0 addNewInput bg-none' placeholder='Write new bullet point' />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    })}
                                    {/**Generate New Section */}
                                    {isGeneratingSection && <GenerateNewSection />}
                                    <AddSectionDropdown text="Add section" items={newSectionItems} onSelect={handleSelectNewSectionItem} />
                                </div>
                                : !kitTemplateData?.id ? <div className="w-full h-[34.5rem] flex flex-col justify-center items-center">
                                    <LazyLoader />
                                    <h3 className="mt-2 text-indigo-700">Retrieving Kit..</h3>
                                </div>
                                    : isErrorTemplateData ? <Warning title={errorMessage} message='Please refresh to try again' btnText='Refresh' />
                                        : null
                        }
                        <EvaluationFooterActions
                            saveAsTemplate={saveAsTemplate}
                            isSaveEvaluationDisabled={isSaveBtnDisabled}
                            handleEvaluationSubmit={handleSave}
                            isLoading={isLoadingSaving}
                            isEditing={isEditingKit}
                            isSuccessSavingEvaluation={isSuccessSaving}
                        />
                    </>
                </ModalBody>
            }
            />
    );
};
